import { API } from '../lib/api';
import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';

interface ImageUploadProps {
  projectId: string;
  pageNumber: number;
  onUploadSuccess: (imageUrl: string) => void;
  onUploadError?: (error: string) => void;
  disabled?: boolean;
  className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  projectId,
  pageNumber,
  onUploadSuccess,
  onUploadError,
  disabled = false,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      return 'Veuillez sélectionner un fichier image';
    }

    // Vérifier la taille (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      return 'Le fichier est trop volumineux (10MB max)';
    }

    // Types supportés
    const supportedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!supportedTypes.includes(file.type)) {
      return 'Format non supporté. Utilisez JPG, PNG ou WebP';
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setUploadStatus('error');
      setStatusMessage(validationError);
      if (onUploadError) onUploadError(validationError);
      return;
    }

    setSelectedFile(file);
    
    // Créer un aperçu
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    
    setUploadStatus('idle');
    setStatusMessage('');
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadStatus('idle');
    
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(
        `${API}/api/images/upload/${projectId}/${pageNumber}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const result = await response.json();

      if (response.ok) {
        setUploadStatus('success');
        setStatusMessage('Image uploadée avec succès!');
        onUploadSuccess(result.image.url);
        
        // Reset après succès
        setTimeout(() => {
          setSelectedFile(null);
          setPreviewUrl(null);
          setUploadStatus('idle');
          setStatusMessage('');
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 2000);
      } else {
        throw new Error(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      setUploadStatus('error');
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setStatusMessage(errorMessage);
      if (onUploadError) onUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadStatus('idle');
    setStatusMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      
      const validationError = validateFile(file);
      if (validationError) {
        setUploadStatus('error');
        setStatusMessage(validationError);
        if (onUploadError) onUploadError(validationError);
        return;
      }

      setSelectedFile(file);
      
      // Créer un aperçu
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      setUploadStatus('idle');
      setStatusMessage('');
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {!selectedFile ? (
        <div
          className={`
            border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer
            hover:border-gray-400 transition-colors
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-children">
            <span className="font-semibold text-magical-purple">Cliquez pour sélectionner</span>{' '}
            ou glissez-déposez une image
          </p>
          <p className="text-xs text-gray-500 mt-1 font-children">
            JPG, PNG ou WebP - 10MB max
          </p>
        </div>
      ) : (
        <div className="border-2 border-gray-200 rounded-lg p-4">
          {/* Aperçu de l'image */}
          {previewUrl && (
            <div className="mb-4">
              <img
                src={previewUrl}
                alt="Aperçu"
                className="w-full max-w-xs mx-auto rounded-lg shadow-md"
              />
            </div>
          )}

          {/* Informations du fichier */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-children font-semibold text-gray-800">
                {selectedFile.name}
              </p>
              <p className="font-children text-xs text-gray-500">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            <button
              onClick={handleCancel}
              disabled={isUploading}
              className="text-gray-400 hover:text-red-500 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages de statut */}
          {statusMessage && (
            <div className={`flex items-center mb-4 p-2 rounded-lg ${
              uploadStatus === 'success' 
                ? 'bg-green-50 text-green-700' 
                : uploadStatus === 'error'
                ? 'bg-red-50 text-red-700'
                : 'bg-blue-50 text-blue-700'
            }`}>
              {uploadStatus === 'success' && <CheckCircle className="h-4 w-4 mr-2" />}
              {uploadStatus === 'error' && <AlertCircle className="h-4 w-4 mr-2" />}
              <span className="font-children text-sm">{statusMessage}</span>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex gap-3">
            <button
              onClick={handleUpload}
              disabled={isUploading || disabled}
              className={`
                flex-1 btn btn-primary
                ${isUploading ? 'opacity-75 cursor-not-allowed' : ''}
              `}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Uploader l'image
                </>
              )}
            </button>
            
            <button
              onClick={handleCancel}
              disabled={isUploading}
              className="btn btn-secondary"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
